// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { AsyncPipe, NgIf } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  Input,
  OnChanges,
  OnInit,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import moment from 'moment';
import { combineLatest, map, startWith, take } from 'rxjs';
import { PatternMatchDataService } from 'src/app/modules/patternmatch/services/pattern-match-data/pattern-match-data.service';

@Component({
  selector: 'app-srsrefreshbutton',
  templateUrl: './srsRefreshButton.component.html',
  styleUrls: ['./srsRefreshButton.component.scss'],
  standalone: true,

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [NgIf, AsyncPipe, TranslateModule],
})
export class SRSRefreshButtonComponent implements OnInit, OnChanges {
  @Input({ required: true }) systemSerialNumber!: string;
  @Input({ required: true }) systemMaterialNumber!: number;
  @Input({ required: true }) systemsoftwareversion!: string;

  srsButtonToggle$ = this.pmService.srsButtonToggle.lastSuccessfulData$.pipe(
    map((d) => !!d),
  );
  srsLastTriggerTimeDisplay$ =
    this.pmService.srsLastTriggerTime.lastSuccessfulData$.pipe(
      map((d) => moment(d.dateTime).fromNow()),
    );

  trigger = this.pmService.srsTriggerCollection;
  srsTriggerResult$ = this.trigger.data$;

  icon$ = combineLatest({
    loading: this.trigger.loading$,
    result: this.trigger.data$,
  }).pipe(
    startWith({ loading: false, result: undefined } as const),
    map(({ loading, result }) => {
      if (!result && !loading) {
        return '';
      }
      if (loading) {
        return 'nothing';
      }
      if (result) {
        if (result.success) {
          return 'checkmark';
        } else {
          return 'error';
        }
      }
    }),
  );

  error$ = combineLatest({
    error: this.trigger.error$.pipe(startWith(undefined)),
    result: this.trigger.data$,
  }).pipe(
    map(({ error, result }) => {
      if (result && !result.success) {
        return result.resultMessage;
      }
      return error ? 'An Error occured' : undefined;
    }),
  );

  constructor(protected pmService: PatternMatchDataService) {}

  ngOnInit(): void {
    // clear button when reinitializing
    this.trigger.reset();
  }
  ngOnChanges(): void {
    this.pmService.srsButtonToggle.request();
    this.pmService.srsLastTriggerTime.request({
      systemSerialNumber: this.systemSerialNumber,
    });
  }

  public triggerDataCollection(): void {
    this.trigger
      .request(
        {
          systemSerialNumber: this.systemSerialNumber,
          systemMaterialNumber: this.systemMaterialNumber,
          systemSoftwareVersion: this.systemsoftwareversion,
        },
        { clearStaleData: true, clearStaleError: true },
      )
      .pipe(take(1))
      .subscribe(() => {
        // retrigger latest refresh time once done
        this.pmService.srsLastTriggerTime.request(
          {
            systemSerialNumber: this.systemSerialNumber,
          },
          { clearStaleData: true, clearStaleError: true },
        );
      });
  }
}
